import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Copy, Check, Link as LinkIcon, Users, Loader2, Share2, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvitationsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, loginWithGoogle } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchInvitations();
      fetchStats();
    } else if (!authLoading && !isAuthenticated) {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated]);

  const fetchInvitations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/invitations/my-invitations`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setInvitations(data.invitations || []);
      }
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/invitations/stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const createInvitation = async () => {
    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/invitations/create`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const newInvite = await response.json();
        setInvitations(prev => [newInvite, ...prev]);
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to create invitation:', error);
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const shareInvite = async (invite) => {
    const shareData = {
      title: 'Join AMP Prediction Market',
      text: `${invite.inviter_name} invited you to join AMP - the prediction market simulator!`,
      url: invite.invite_url
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        // User cancelled share
      }
    } else {
      copyToClipboard(invite.invite_url, invite.id);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#050810]">
        <Header />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="h-8 w-8 text-[#3B82F6] animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050810]">
        <Header />
        <div className="max-w-md mx-auto px-4 py-20">
          <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-8 text-center">
            <UserPlus className="h-12 w-12 text-[#3B82F6] mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Invite Friends</h1>
            <p className="text-gray-400 mb-6">
              Sign in to create invite links and share AMP with your friends!
            </p>
            <Button
              onClick={loginWithGoogle}
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
            >
              Sign in to Continue
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810]">
      <Header />
      
      {/* Header Section */}
      <div className="bg-gradient-to-b from-[#131b2e] to-[#050810] border-b border-[#1e2a45]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <UserPlus className="h-8 w-8 text-[#3B82F6]" />
              <div>
                <h1 className="text-2xl font-bold text-white">Invite Friends</h1>
                <p className="text-gray-400 text-sm">Share AMP and earn badges</p>
              </div>
            </div>
            <Button
              onClick={createInvitation}
              disabled={creating}
              data-testid="create-invite-btn"
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
            >
              {creating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <LinkIcon className="h-4 w-4 mr-2" />
              )}
              Create Invite Link
            </Button>
          </div>
          
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="bg-[#1e2a45]/50 rounded-xl p-4 border border-[#1e2a45]">
                <div className="flex items-center gap-2 mb-2">
                  <LinkIcon className="h-5 w-5 text-[#3B82F6]" />
                  <span className="text-gray-400 text-sm">Links Created</span>
                </div>
                <span className="text-2xl font-bold text-white">{stats.total_invitations_created}</span>
              </div>
              <div className="bg-[#1e2a45]/50 rounded-xl p-4 border border-[#1e2a45]">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-5 w-5 text-green-400" />
                  <span className="text-gray-400 text-sm">Friends Joined</span>
                </div>
                <span className="text-2xl font-bold text-white">{stats.total_users_invited}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Info Card */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <div className="bg-gradient-to-r from-[#3B82F6]/10 to-[#8B5CF6]/10 border border-[#3B82F6]/30 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-2">How it works</h2>
          <ul className="text-gray-400 text-sm space-y-2">
            <li className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#3B82F6]/20 text-[#3B82F6] flex items-center justify-center text-xs">1</span>
              Create an invite link (unlimited links allowed)
            </li>
            <li className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#3B82F6]/20 text-[#3B82F6] flex items-center justify-center text-xs">2</span>
              Share the link with friends via email, social media, or messaging
            </li>
            <li className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#3B82F6]/20 text-[#3B82F6] flex items-center justify-center text-xs">3</span>
              Friends sign up and receive a special "Invited" badge
            </li>
            <li className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-xs">✓</span>
              No subscription required - they can vote on ideas immediately!
            </li>
          </ul>
        </div>

        {/* Invitations List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-[#3B82F6] animate-spin" />
          </div>
        ) : invitations.length === 0 ? (
          <div className="text-center py-20">
            <LinkIcon className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No invitations yet</h3>
            <p className="text-gray-400 mb-6">Create your first invite link to share with friends!</p>
            <Button
              onClick={createInvitation}
              disabled={creating}
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
            >
              Create Invite Link
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-white">Your Invite Links</h2>
            {invitations.map(invite => (
              <div
                key={invite.id}
                className="bg-[#131b2e] rounded-xl border border-[#1e2a45] p-4"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <code className="text-sm text-[#3B82F6] bg-[#1e2a45] px-2 py-1 rounded">
                        {invite.invite_code}
                      </code>
                      <span className="text-xs text-gray-500">
                        {invite.uses_count} {invite.uses_count === 1 ? 'use' : 'uses'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400 truncate">{invite.invite_url}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Created {new Date(invite.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => copyToClipboard(invite.invite_url, invite.id)}
                      size="sm"
                      variant="outline"
                      className="border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45]"
                      data-testid={`copy-invite-${invite.id}`}
                    >
                      {copiedId === invite.id ? (
                        <Check className="h-4 w-4 text-green-400" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      onClick={() => shareInvite(invite)}
                      size="sm"
                      className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
                    >
                      <Share2 className="h-4 w-4 mr-1" />
                      Share
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default InvitationsPage;
